/**
 * Project thumbnail image, used by the project cards and the projects table.
 *
 * @category components
 */

import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { Project } from "_/data/projects";

import * as S from "./styled";

// Placeholder image of a wireframe cube, used for project previews.
//
// If the `$pulse` prop is set to `true`, the cube will have a gentle pulse
// animation applied.
const PlaceholderImage = ({
  $visibility,
  $pulse,
}: {
  $visibility: "hidden" | "visible";
  $pulse: boolean;
}) => (
  <S.PlaceholderSVG
    $visibility={$visibility}
    $pulse={$pulse}
    width="960"
    height="960"
    viewBox="0 0 960 960"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    xmlns="http://www.w3.org/2000/svg"
  >
    <line x1="333" y1="37" x2="333" y2="628" />
    <line x1="43" y1="332" x2="43" y2="921" />
    <line x1="917" y1="628" x2="917" y2="37" />
    <line x1="627" y1="921" x2="627" y2="332" />
    <line x1="628" y1="331" x2="42" y2="331" />
    <line x1="918" y1="38" x2="332" y2="38" />
    <line x1="332" y1="627" x2="918" y2="627" />
    <line x1="42" y1="920" x2="628" y2="920" />
    <line x1="332.711" y1="37.7035" x2="42.7107" y2="330.703" />
    <line x1="333.711" y1="626.703" x2="43.7107" y2="919.703" />
    <line x1="916.479" y1="38.3338" x2="626.711" y2="331.703" />
    <line x1="917.313" y1="627.205" x2="627.252" y2="920.217" />
  </S.PlaceholderSVG>
);

/**
 * How long after project creation we assume a preview may still be generating.
 * This typically takes just a few seconds, but may be delayed during periods
 * of heavy worker load.
 */
const FRESH_WINDOW_MS = 30_000;
const POLL_INTERVAL_MS = 2_000;

// Project thumbnail image.
//
// This will attempt to load the project preview, and if the image is not yet ready (e.g.
// immediately after project creation), a placeholder will be shown with a subtle pulsing
// animation to show that the image is being generated. If the image has not been generated
// after the `FRESH_WINDOW_MS` interval, it will become static and assumed that the preview
// generation has failed.
export const ProjectThumbnail = ({
  project,
  size = "100px",
}: {
  project: Project;
  size?: string;
}) => {
  const [loaded, setLoaded] = useState(false);

  const thumbnailUrl = `/api/v0/projects/${project.id}/thumbnail?q=${project.updatedAt}`;

  const isFresh = dayjs().diff(dayjs(project.createdAt)) < FRESH_WINDOW_MS;
  const isGenerating = !loaded && isFresh;

  useEffect(() => {
    if (!isGenerating) {
      return undefined;
    }

    const id = setInterval(() => {
      if (!isFresh) {
        clearInterval(id);
        return;
      }

      const probe = new Image();
      probe.onload = () => setLoaded(true);
      probe.src = thumbnailUrl;
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(id);
    };
  }, [isGenerating, isFresh, thumbnailUrl]);

  return (
    <S.Container $size={size}>
      <PlaceholderImage
        $visibility={loaded ? "hidden" : "visible"}
        $pulse={isGenerating}
      />
      <S.Image
        key={loaded ? "loaded" : "pending"}
        src={thumbnailUrl}
        $visibility={loaded ? "visible" : "hidden"}
        onLoad={() => setLoaded(true)}
      />
    </S.Container>
  );
};
